import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import * as authApi from '../api/auth';
import styles from './AuthPages.module.css';

/**
 * ResetPasswordPage — handles the Supabase password-reset redirect.
 *
 * Supabase appends recovery session tokens to the URL hash:
 *   /reset-password#access_token=...&refresh_token=...&type=recovery
 *
 * Flow:
 *   1. Extract tokens from hash, call setSession() to authenticate
 *   2. Show new-password form (Screen 3)
 *   3. On submit, call POST /auth/update-password with the new password
 *   4. Show success state (Screen 4), then navigate to /
 *
 * Error paths:
 *   - Missing/wrong-type tokens → 'invalid' state (link expired or wrong link)
 *   - setSession failure → 'invalid' state
 *   - API failure → inline error on the form
 *
 * Figma: node 2432-11365, screens Reset-PW-3 and Reset-PW-4.
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSession } = useAuthStore();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageState, setPageState] = useState('loading'); // 'loading' | 'form' | 'success' | 'invalid'
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash.substring(1); // strip leading #
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type');

    if (!accessToken || !refreshToken || type !== 'recovery') {
      setPageState('invalid');
      return;
    }

    setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(() => setPageState('form'))
      .catch(() => setPageState('invalid'));
  }, [setSession]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await authApi.updatePassword(password);
      setPageState('success');
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main className={styles.page}>
      {/* Left panel — illustration + logo */}
      <div className={styles.authLeft}>
        <img src="/assets/auth-illustration.webp" alt="" className={styles.bgImage} />
        <img src="/assets/wanderwords-logo.svg" alt="WanderWords" className={styles.logo} />
      </div>

      {/* Right panel — form + footer */}
      <div className={styles.authRight}>
        <div className={styles.content}>

        {/* ── State: loading (session being established) ── */}
        {pageState === 'loading' && (
          <p className={styles.switchPrompt} aria-live="polite">Verifying your link…</p>
        )}

        {/* ── State: invalid (expired or missing tokens) ── */}
        {pageState === 'invalid' && (
          <>
            <h1 className={styles.cardTitle}>Link Expired</h1>
            <p className={styles.switchPrompt}>
              This password reset link has expired or has already been used.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={() => navigate('/forgot-password')}
              >
                Request a New Link
              </button>
              <p className={styles.switchPrompt}>
                <Link to="/login" className={styles.switchLink}>Back to Login</Link>
              </p>
            </div>
          </>
        )}

        {/* ── State: form (new password entry) ── */}
        {pageState === 'form' && (
          <>
            {error && (
              <div className={styles.errorBanner} role="alert">{error}</div>
            )}

            <h1 className={styles.cardTitle}>Reset Password</h1>

            <form id="reset-form" onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label htmlFor="reset-password" className={styles.label}>
                  {t('auth.password')}
                </label>
                <input
                  id="reset-password"
                  type="password"
                  placeholder={t('auth.passwordHint')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className={styles.input}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="reset-confirm" className={styles.label}>
                  Confirm Password
                </label>
                <input
                  id="reset-confirm"
                  type="password"
                  placeholder={t('auth.passwordHint')}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  className={styles.input}
                />
              </div>
            </form>

            <div className={styles.actions}>
              <button
                type="submit"
                form="reset-form"
                disabled={isLoading}
                className={styles.submitBtn}
              >
                {isLoading ? 'Resetting…' : 'Reset Password'}
              </button>
              <p className={styles.switchPrompt}>
                <Link to="/login" className={styles.switchLink}>Back to Login</Link>
              </p>
            </div>
          </>
        )}

        {/* ── State: success ── */}
        {pageState === 'success' && (
          <>
            <h1 className={styles.cardTitle}>Password Reset!</h1>
            <p className={styles.switchPrompt}>
              Your password has been successfully reset.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={() => navigate('/')}
              >
                {t('auth.login')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Page footer */}
      <footer className={styles.pageFooter}>
        <a href="https://wanderwords.co/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
          Terms of Service
        </a>
        <a href="https://wanderwords.co/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
          Privacy Policy
        </a>
        <p className={styles.footerCopyright}>&copy; 2026 Bricksquare Pte. Ltd.</p>
        </footer>
      </div>
    </main>
  );
}
